import { useRegisterSW } from 'virtual:pwa-register/react';
import { ReactComponent as CloseIcon } from '@assets/icons/close.svg';
import { ReactComponent as DownloadIcon } from '@assets/icons/download_line.svg';
import { colors } from '@theme/colors';
import { useEffect, useState } from 'react';
import styled from 'styled-components';

export const PWAInstallPrompt = () => {
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);

  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegistered(r: any) {
      console.log('SW Registered:', r);
    },
    onRegisterError(error: any) {
      console.log('SW registration error', error);
    },
  });

  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);

      const hasPromptBeenDismissed = sessionStorage.getItem('pwa-prompt-dismissed');
      if (!hasPromptBeenDismissed) {
        setTimeout(() => {
          setShowInstallPrompt(true);
        }, 2000);
      }
    };

    window.addEventListener('beforeinstallprompt', handler);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      console.log('User accepted the install prompt');
    }

    setDeferredPrompt(null);
    setShowInstallPrompt(false);
  };

  const handleDismiss = () => {
    setShowInstallPrompt(false);
    sessionStorage.setItem('pwa-prompt-dismissed', 'true');
  };

  const handleUpdate = () => {
    updateServiceWorker(true);
  };

  if (needRefresh) {
    return (
      <UpdateBanner>
        <UpdateContent>
          <UpdateText>Une nouvelle version est disponible !</UpdateText>
          <UpdateActions>
            <UpdateButton onClick={handleUpdate}>Mettre à jour</UpdateButton>
            <UpdateDismissButton onClick={() => setNeedRefresh(false)}>
              <CloseIcon />
            </UpdateDismissButton>
          </UpdateActions>
        </UpdateContent>
      </UpdateBanner>
    );
  }

  if (!showInstallPrompt || !deferredPrompt) return null;

  return (
    <InstallBanner>
      <InstallContent>
        <InstallMainContent>
          <InstallIcon>
            <DownloadIcon />
          </InstallIcon>
          <InstallTextContainer>
            <InstallTitle>Installer Stay or Go</InstallTitle>
            <InstallDescription>
              {"Installez l'application pour un accès rapide et une meilleure expérience"}
            </InstallDescription>
          </InstallTextContainer>
        </InstallMainContent>
        <InstallActions>
          <InstallButton onClick={handleInstall}>Installer</InstallButton>
          <DismissButton onClick={handleDismiss}>
            <CloseIcon />
          </DismissButton>
        </InstallActions>
      </InstallContent>
    </InstallBanner>
  );
};

const InstallBanner = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: ${colors.primary};
  color: ${colors.white};
  box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  animation: slideUp 0.3s ease-out;

  @keyframes slideUp {
    from {
      transform: translateY(100%);
    }
    to {
      transform: translateY(0);
    }
  }
`;

const InstallContent = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 20px;
  max-width: 1200px;
  margin: 0 auto;

  @media (max-width: 500px) {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }
`;

const InstallMainContent = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
  min-width: 0;
`;

const InstallIcon = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 32px;
    height: 32px;
  }
`;

const InstallTextContainer = styled.div`
  flex: 1;
  min-width: 0;
`;

const InstallTitle = styled.h3`
  margin: 0 0 4px;
  font-size: 16px;
  font-weight: 600;
  color: ${colors.white};
`;

const InstallDescription = styled.p`
  margin: 0;
  font-size: 14px;
  color: ${colors.white};
  opacity: 0.9;
  line-height: 1.4;
`;

const InstallActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;

  @media (max-width: 500px) {
    width: 100%;
  }
`;

const InstallButton = styled.button`
  background: ${colors.white};
  color: ${colors.primary};
  border: none;
  padding: 10px 24px;
  border-radius: 38px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${colors.primary10};
    color: ${colors.primary200};
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 500px) {
    flex: 1;
  }
`;

const DismissButton = styled.button`
  background: transparent;
  border: none;
  color: ${colors.white};
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s ease;

  svg {
    width: 20px;
    height: 20px;
    fill: ${colors.white};
  }

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    color: ${colors.white};

    svg {
      fill: ${colors.white};
    }
  }

  @media (max-width: 500px) {
    position: absolute;
    top: 12px;
    right: 12px;
    padding: 4px;
  }
`;

const UpdateBanner = styled(InstallBanner)`
  background: ${colors.primary200};
`;

const UpdateContent = styled(InstallContent)``;

const UpdateText = styled.p`
  margin: 0;
  flex: 1;
  font-weight: 600;
  font-size: 14px;
  color: ${colors.white};
`;

const UpdateActions = styled(InstallActions)``;

const UpdateButton = styled(InstallButton)``;

const UpdateDismissButton = styled(DismissButton)``;
